/**
 * The plan-change announcement: the bot card scripts/announce-plan-change.ts
 * sends, and when the in-app banner (`PlanChangeBanner.vue`) shows.
 *
 * Plain TypeScript, no Vue, so the send script (Node) and the tests can use it.
 *
 * Who it is for is decided elsewhere, once: `isLegacyFreeTenant()` in
 * shared-types — the tenants scripts/gift-free-packs.ts moves on
 * FREE_PLAN_CHANGE_AT. The API reports it as `UsageStatus.legacyFreeAllowance`.
 */
import {
  docsUrl,
  FREE_PLAN_CHANGE_AT,
  MS_PER_DAY,
  type SupportedLocale,
  type UsageStatus,
} from '@lark-automadoc/shared-types'

import { documentPacksUSD, seatPricingUSD } from '../data/pricing'
import { getMessages } from '../locales'
import { botCardAllLanguages, type BotCardSpec } from '../bot/card'

/** Recorded with every bot message sent (announcement_deliveries), and part of the banner's dismissal key. */
export const PLAN_CHANGE_ANNOUNCEMENT_ID = 'plan-change-2026-11'

/** How long after the change everyone sees the banner. */
export const PLAN_CHANGE_BANNER_DAYS_AFTER = 14

/** Where the "See what's new" button goes. */
export const PLAN_CHANGE_DOCS_PATH = 'reference/changelog'

/**
 * - `before`: until the change, to a legacy Free tenant only — what changes for them.
 * - `after`: for PLAN_CHANGE_BANNER_DAYS_AFTER days after it, to everyone. By then
 *   the moved tenants are no longer told apart from any other Free tenant (the
 *   gift script changed their row), so it is about what is new.
 */
export type PlanChangeBannerVariant = 'before' | 'after'

export function planChangeBannerVariant(
  usage: Pick<UsageStatus, 'legacyFreeAllowance'> | null | undefined,
  now: number = Date.now(),
): PlanChangeBannerVariant | null {
  if (now < FREE_PLAN_CHANGE_AT) return usage?.legacyFreeAllowance === true ? 'before' : null
  if (now < FREE_PLAN_CHANGE_AT + PLAN_CHANGE_BANNER_DAYS_AFTER * MS_PER_DAY) return 'after'
  return null
}

/** The cheapest seat and pack, as quoted on the pricing page, so the copy cannot drift from it. */
export function planChangePrices(locale: SupportedLocale): { seatPrice: string; packPrice: string; packSize: string } {
  const pack = documentPacksUSD[0]
  return {
    seatPrice: `$${seatPricingUSD.starter.monthly}`,
    packPrice: `$${pack.price}`,
    packSize: new Intl.NumberFormat(locale).format(pack.size),
  }
}

const fill = (message: string, values: Record<string, string>): string =>
  message.replace(/\{(\w+)\}/g, (whole, name: string) => values[name] ?? whole)

export interface PlanChangeCardLinks {
  /** The environment's docs origin (the API's DOCS_URL); unset is production, which a staging send must not fall back to. */
  docsOrigin?: string | null
  /** The company portal's Billing page, as the reader's Lark client should open it. */
  billingUrl: string
  /**
   * The reader's notification preferences (the portal's Notifications page). Product news
   * is a category a person may turn off: the sender skips anyone who did (contact center,
   * `contactAllowed()` / `canSend()`), and the footer links here.
   */
  manageNotificationsUrl?: string
}

function cardSpec(locale: SupportedLocale, links: PlanChangeCardLinks): BotCardSpec {
  const m = getMessages(locale)
  const features = [
    m.announcementPlanChangeFeatureOffice,
    m.announcementPlanChangeFeatureDesigner,
    m.announcementPlanChangeFeatureCombined,
    m.announcementPlanChangeFeatureAttachments,
    m.announcementPlanChangeFeatureTables,
    m.announcementPlanChangeFeatureApprovals,
    m.announcementPlanChangeFeatureOtherApps,
    m.announcementPlanChangeFeatureEsign,
    m.announcementPlanChangeFeatureApi,
    m.announcementPlanChangeFeaturePlatforms,
  ]
  const whatsNew = docsUrl(PLAN_CHANGE_DOCS_PATH, locale, links.docsOrigin)
  return {
    kind: 'news',
    title: m.announcementPlanChangeTitle,
    markdown: [
      `**${m.announcementPlanChangeIntro}**\n${features.map((f) => `• ${f}`).join('\n')}`,
      `**${m.announcementPlanChangeButHeading}**\n${m.announcementPlanChangeAllowance}`,
      m.announcementPlanChangeWriteBack,
      m.announcementPlanChangeAfter,
      fill(m.announcementPlanChangeNeedMore, planChangePrices(locale)),
    ],
    primary: { label: m.announcementPlanChangeWhatsNew, url: whatsNew },
    secondary: [{ label: m.announcementPlanChangeBilling, url: links.billingUrl }],
    footer: { locale, helpUrl: whatsNew, manageNotificationsUrl: links.manageNotificationsUrl },
  }
}

/**
 * The interactive card (message card JSON 1.0) in all seven languages, on the bot's card
 * kit (shared-ui bot/): Lark shows the reader's client language, since AutomaDoc stores
 * no language per user.
 */
export function planChangeCard(links: PlanChangeCardLinks): Record<string, unknown> {
  return botCardAllLanguages((locale) => cardSpec(locale, links))
}
